import cx from 'classnames'
import { noop } from 'lodash'
import MessageBox from 'components/base/MessageBox'
import { TranslationContext } from 'contexts/TranslationContext'
import { useEditorActions } from 'hooks/useEditorActions'
import React, { useCallback, useContext, useRef } from 'react'
import { useSelector } from 'react-redux'
import * as Actions from '~/actions'
import { ConfirmationDialog } from '~/components/base/ConfirmationDialog/ConfirmationDialog'
import {
  getFPSByRefVideo,
  timelineTimeToSeconds,
  getNameFromPath,
  getPathFromPathName
} from '~/Util'
import { useAction, useModalDialog } from '../../hooks'
import { getAssets, getPrefferedOutputFormat, getProjectDuration, getReferenceVideoAsset } from '~/selectors'
import Button from '../base/Button'
import './TimelineFooter.scss'
import ZoomSlider from './ZoomSlider'
import { NW_JS_APP_PROFILES, PLAYER_TYPE } from '~/enums'
import { DEFAULT_PROJECT_OUTPUT_NAME } from '~/constant'
import { DROP_CONTAINER } from '~/config/constants/ui'
import { Orientation } from '~/types/userInterface'
import { TransformUIDropContainer } from '~/components/TransformUIDropContainer'
import { TIME_CODE_CONTAINER_LOCATION } from '~/config/constants/timecode'
import { TimeCode } from '~/components/Common/TimeCode'

function TimelineFooter({ saveDisabled }) {
  const { t } = useContext(TranslationContext)
  const assets = useSelector(getAssets)
  const showSaveSettings = useSelector(state => state.mainView.showSaveSettings)
  const projectDuration = useSelector(getProjectDuration)
  const refVideo = useSelector(getReferenceVideoAsset)
  const currentFps = getFPSByRefVideo(refVideo)
  const saveProject = useAction(Actions.saveProject.saveProject)
  const format = useSelector(getPrefferedOutputFormat)
  const outputName = useSelector(state => state.project.outputName === DEFAULT_PROJECT_OUTPUT_NAME
    ? t('PUBLISH_SETTINGS_OUTPUT_NAME')
    : state.project.outputName)
  const outputPath = useSelector(state => state.project.outputPath)
  const openSettings = useAction(Actions.mainView.showSaveSettings, true)

  const savingResultDialog = useModalDialog({})

  const [ unableToSave, setUnableToSave ] = React.useState(false)

  const onUnableToSaveMessageClose = useCallback(() => {
    setUnableToSave(false)
  }, [])

  const inputRef = useRef()
  const inputStyle = { display: 'none' }

  const { onPublish, isRenderingSupported } = useEditorActions({ inputRef })
  const setCurAbsPathName = useAction(Actions.project.setAbsFilePath)

  const resetInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [])

  const prepareTask = useAction(Actions.project.prepareTask)
  const onOutputFileSelected = useCallback(e => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0]
      const pathName = file.path || file.name
      const pathNameWOExt = pathName.slice(0, pathName.lastIndexOf('.'))
      prepareTask(getNameFromPath(pathNameWOExt), getPathFromPathName(pathNameWOExt))
      setCurAbsPathName(pathNameWOExt)
      resetInput()
      openSettings()
    }
  }, [ setCurAbsPathName, openSettings, prepareTask, resetInput ])

  const onClickSave = async () => {
    await saveProject()
    savingResultDialog.open()
  }

  const handleClickPublish = () => {
    if (isRenderingSupported) {
      resetInput()
      setTimeout(() => {
        onPublish()
      }, 0)
    } else {
      setUnableToSave(true)
    }
  }

  return (
    <>
      <TransformUIDropContainer
        orientation={Orientation.Horizontal}
        dropContainer={DROP_CONTAINER.FOOTER}
      />
      <div className="timeline-footer">
        <div className="timeline-footer__slider">
          <ZoomSlider />
        </div>
        <div className="timeline-footer__duration">
          {t('TIMELINE_FOOTER_TEXT_DURATION')}
          :
          <TimeCode
            progress={timelineTimeToSeconds(projectDuration)}
            containerLocation={TIME_CODE_CONTAINER_LOCATION.TIMELINE}
            onRewind={noop}
            fps={currentFps}
            previewPlayerType={PLAYER_TYPE.TIMELINE}
            readonly
          />
        </div>
        <div className={cx('timeline-footer__btns', { 'timeline-footer__btns--disabled': saveDisabled })}>
          <If condition={__CFG__.SAVE_PROJECT_MENU.SAVE_PROJECT_BUTTON}>
            <div className="timeline-footer__btn">
              <Button
                className="timeline__btn-save"
                disabled={assets.length === 0 || showSaveSettings}
                primary
                onClick={onClickSave}
                title={t('TIMELINE_FOOTER_BTN_SAVE_TOOLTIP')}
                data-lang-id="TIMELINE_FOOTER_BTN_SAVE"
              >
                {t('TIMELINE_FOOTER_BTN_SAVE')}
              </Button>
            </div>
          </If>
          <div className="timeline-footer__btn">
            <If condition={NW_JS_APP_PROFILES.includes(__APP_PROFILE__)}>
              <input ref={inputRef} type="file" value="" accept={`.${format}`} nwworkingdir={outputPath} nwsaveas={outputName} style={inputStyle} onChange={onOutputFileSelected} />
            </If>
            <Button
              className="timeline__btn-save"
              disabled={assets.length === 0 || showSaveSettings}
              primary
              onClick={handleClickPublish}
              title={NW_JS_APP_PROFILES.includes(__APP_PROFILE__) ? t('TIMELINE_FOOTER_BTN_SAVE_TOOLTIP') : t('TIMELINE_FOOTER_BTN_PUBLISH_TOOLTIP')}
              data-lang-id={NW_JS_APP_PROFILES.includes(__APP_PROFILE__) ? 'TIMELINE_FOOTER_BTN_SAVE' : 'TIMELINE_FOOTER_BTN_PUBLISH'}
            >
              {NW_JS_APP_PROFILES.includes(__APP_PROFILE__) ? t('TIMELINE_FOOTER_BTN_SAVE') : t('TIMELINE_FOOTER_BTN_PUBLISH')}
            </Button>
          </div>
        </div>
      </div>
      <If condition={savingResultDialog.active}>
        <ConfirmationDialog
          onCancel={savingResultDialog.cancel}
          closeBtnText="Close"
          title="The project was saved!"
        />
      </If>
      {unableToSave && (
        <MessageBox
          title={t('MESSAGE_BOX_UNABLE_TO_PUBLISH')}
          message={t('MESSAGE_BOX_VIDEO_ASSET_REQUIRED')}
          onClose={onUnableToSaveMessageClose}
        />
      )}
    </>
  )
}

export default TimelineFooter
